import React, { useEffect, useState, useMemo } from "react";
import Shell, { StatCard, Section } from "@/pages/Shell";
import { api, formatError, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { filterAssignableUsers } from "@/lib/leadAssignees";
import { TasksAI } from "@/pages/constructor/ConstructorDashboard";
import { AlertsBoard } from "@/components/NextBestAction";
import ActionCenter, { DashboardActionZone } from "@/components/ActionCenter";
import { LEAD_STATUSES, normalizeStatus, hasAnySignal } from "@/lib/leadStatuses";
import { VISIBLE_PIPELINE_WITH_COLD, visibleStep } from "@/lib/leadPipeline";
import LeadCard from "@/components/leads/LeadCard";
import LeadSidePanel from "@/components/leads/LeadSidePanel";
import PipelineStageAccordion from "@/components/leads/PipelineStageAccordion";
import BulkWhatsappModal from "@/components/marketing/BulkWhatsappModal";
import FollowUpBoard from "@/components/FollowUpBoard";
import { useFollowUpCounts } from "@/hooks/useFollowUpCounts";
import { getLeadPhone, isValidWhatsappPhone } from "@/lib/bulkWhatsapp";
import {
  Megaphone, Funnel, ListChecks, Plus, Bell, Warning, DownloadSimple, Rows, SquaresFour,
  WhatsappLogo, ClockCounterClockwise, Lightning,
} from "@phosphor-icons/react";

const PIPELINE_VIEW_KEY = "crm.pipeline_view"; // localStorage

/** Granular attention facets (UI-only, derived from existing backend `signals`). */
const ATTENTION_FACETS = [
  { key: "missing_next_action", label: "Sin próxima acción", tone: "amber" },
  { key: "overdue_action",      label: "Vencida",            tone: "red"   },
  { key: "unassigned",          label: "Sin responsable",    tone: "amber" },
];

const ACCENT = "#C28F3C";

export default function MarketingDashboard() {
  const [tab, setTab] = useState("overview");
  // Small red dot on the follow-ups tab when there are overdue+due_today
  // items to attend. Silent-on-error via the shared hook — same signal used
  // by the admin sidebar badge so both surfaces stay in sync.
  const { counts: followUpCounts } = useFollowUpCounts();
  const followUpBadge = followUpCounts
    ? (followUpCounts.overdue || 0) + (followUpCounts.due_today || 0)
    : 0;
  const tabs = [
    { key: "overview", label: "Resumen", icon: Megaphone },
    { key: "action-center", label: "Centro de acción", icon: Lightning },
    { key: "alerts", label: "Alertas", icon: Bell },
    { key: "leads", label: "Leads & funnel", icon: Funnel },
    { key: "follow-ups", label: "Seguimientos", icon: ClockCounterClockwise, dot: followUpBadge > 0 },
    { key: "tasks", label: "Tareas IA", icon: ListChecks },
  ];
  return (
    <Shell tabs={tabs} activeTab={tab} onTabChange={setTab}>
      {tab === "overview" && <Overview onNavigate={setTab} />}
      {tab === "action-center" && <ActionCenter lang="es" />}
      {tab === "alerts" && <AlertsBoard onNavigate={setTab} />}
      {tab === "leads" && <LeadsBoard />}
      {tab === "follow-ups" && <FollowUpBoard showBackfill={false} />}
      {tab === "tasks" && <TasksAI />}
    </Shell>
  );
}

function Overview({ onNavigate }) {
  const [leads, setLeads] = useState([]);
  useEffect(() => { api.get("/leads").then(r => setLeads(r.data)); }, []);

  const newLeads = leads.filter(l => normalizeStatus(l.status) === "new").length;
  const inFollowup = leads.filter(l => normalizeStatus(l.status) === "in_followup").length;
  const reserved = leads.filter(l => normalizeStatus(l.status) === "reserved").length;

  const bySource = leads.reduce((acc, l) => {
    const s = l.source || "desconocido";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      <DashboardActionZone
        lang="es"
        onViewAll={() => onNavigate?.("action-center")}
        onViewFollowUps={() => onNavigate?.("follow-ups")}
      />

      <Section title="Panel de marketing" subtitle="El pulso de tus leads y conversiones.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Leads totales" value={leads.length} accent={ACCENT} testid="stat-leads-total" />
          <StatCard label="Leads nuevos" value={newLeads} accent={ACCENT} testid="stat-leads-new" />
          <StatCard label="En seguimiento" value={inFollowup} accent={ACCENT} testid="stat-leads-followup" />
          <StatCard label="Reservados" value={reserved} accent={ACCENT} testid="stat-leads-closed" />
        </div>
      </Section>

      <Section title="Leads por canal">
        <div className="dv-card">
          <div className="space-y-3">
            {Object.entries(bySource).map(([src, count]) => {
              const pct = leads.length ? (count / leads.length) * 100 : 0;
              return (
                <div key={src}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="capitalize font-medium">{src}</span>
                    <span className="text-stone-500">{count} ({pct.toFixed(0)}%)</span>
                  </div>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: ACCENT }} />
                  </div>
                </div>
              );
            })}
            {Object.keys(bySource).length === 0 && <div className="text-stone-400 text-sm">Sin datos.</div>}
          </div>
        </div>
      </Section>
    </div>
  );
}

const OWNER_FILTER_KEY = "crm.leads_owner_filter"; // localStorage

export function LeadsBoard() {
  const { user: currentUser } = useAuth();
  const [leads, setLeads] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", source: "web", interest: "", notes: "" });
  const [msg, setMsg] = useState("");
  const [onlyAttention, setOnlyAttention] = useState(false);
  const [activeFacets, setActiveFacets] = useState(new Set()); // subset of ATTENTION_FACETS keys
  const [openLeadId, setOpenLeadId] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [search, setSearch] = useState("");
  // Bulk WhatsApp selection — Set<lead_id>. Kept independent of every other
  // filter so toggling attention/search doesn't drop what's already picked.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  // Owner filter — "all" | "mine" | "unassigned" | "<user_id>"
  const [ownerFilter, setOwnerFilter] = useState(() => {
    try { return localStorage.getItem(OWNER_FILTER_KEY) || "all"; } catch { return "all"; }
  });
  const setOwnerFilterPersisted = (v) => {
    setOwnerFilter(v);
    try { localStorage.setItem(OWNER_FILTER_KEY, v); } catch { /* noop */ }
  };
  const [viewMode, setViewMode] = useState(() => {
    try {
      const stored = localStorage.getItem(PIPELINE_VIEW_KEY);
      return stored === "kanban" ? "kanban" : "stages";
    } catch { return "stages"; }
  });
  const setViewModePersisted = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem(PIPELINE_VIEW_KEY, mode); } catch { /* noop */ }
  };

  const load = () => api.get(`/leads${includeArchived ? "?include_archived=true" : ""}`)
    .then(r => setLeads(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeArchived]);

  // Assignees (admin + marketing) — used by quick actions and side panel.
  useEffect(() => {
    api.get("/users")
      .then(r => setAssignees(filterAssignableUsers(r.data)))
      .catch(() => {});
  }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", form);
      setShowForm(false); setForm({ name: "", email: "", phone: "", source: "web", interest: "", notes: "" });
      load();
    } catch (e) { setMsg(formatError(e)); }
  };

  /** Generic patch handler — used by LeadCard quick actions and by side panel. */
  const patchLead = async (lead, payload) => {
    await api.patch(`/leads/${lead.lead_id}`, payload);
    load();
  };

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const f of ATTENTION_FACETS) {
      counts[f.key] = leads.filter(l => (l.signals || []).includes(f.key)).length;
    }
    return counts;
  }, [leads]);

  const visibleLeads = useMemo(() => {
    let out = leads;
    // Owner filter applied first so attention counts respect it.
    if (ownerFilter && ownerFilter !== "all") {
      if (ownerFilter === "unassigned") {
        out = out.filter(l => !l.assigned_to_user_id);
      } else if (ownerFilter === "mine") {
        const meId = currentUser?.user_id;
        out = meId ? out.filter(l => l.assigned_to_user_id === meId) : [];
      } else {
        out = out.filter(l => l.assigned_to_user_id === ownerFilter);
      }
    }
    if (!onlyAttention && activeFacets.size === 0) return out;
    if (activeFacets.size > 0) {
      return out.filter(l => {
        const sigs = l.signals || [];
        for (const f of activeFacets) if (sigs.includes(f)) return true;
        return false;
      });
    }
    return out.filter(hasAnySignal);
  }, [leads, onlyAttention, activeFacets, ownerFilter, currentUser]);

  const attentionCount = useMemo(() => leads.filter(hasAnySignal).length, [leads]);

  // Free-text search over the already-filtered visibleLeads (owner + attention
  // + facets). Matches partial substrings, case-insensitive. Fields include
  // the ones the operator types most often when hunting a lead — `notes` is
  // intentionally excluded (noisy) and `status` is left to the existing chips.
  const searchedLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return visibleLeads;
    return visibleLeads.filter(l => {
      const hay = [
        l.name, l.email, l.phone, l.instagram, l.source,
        l.campaign, l.country, l.interest, l.assigned_to_name,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [visibleLeads, search]);

  const toggleFacet = (k) => {
    setActiveFacets(prev => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      // turning on a facet implies attention mode is meaningful — but we keep it
      // independent so users can clear with one click.
      return next;
    });
    if (!onlyAttention) setOnlyAttention(true);
  };

  const clearAttention = () => {
    setOnlyAttention(false);
    setActiveFacets(new Set());
  };

  // ------------------------------------------------------------------
  // Bulk WhatsApp helpers
  // ------------------------------------------------------------------
  const toggleLeadSelected = (lead) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(lead.lead_id)) next.delete(lead.lead_id);
      else next.add(lead.lead_id);
      return next;
    });
  };
  const selectVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const l of searchedLeads) {
        if (!l.archived) next.add(l.lead_id);
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const toggleSelectionMode = () => {
    setSelectionMode((prev) => {
      // Turning selection mode off clears the picks — preference from the
      // spec so the operator restarts cleanly next time.
      if (prev) setSelectedIds(new Set());
      return !prev;
    });
  };
  const selectedLeads = useMemo(
    () => leads.filter((l) => selectedIds.has(l.lead_id)),
    [leads, selectedIds],
  );
  const validSelectedCount = useMemo(
    () => selectedLeads.filter((l) => !l.archived && isValidWhatsappPhone(getLeadPhone(l))).length,
    [selectedLeads],
  );

  return (
    <Section title="Pipeline de leads" subtitle="Pipeline operativo. La columna En seguimiento agrupa leads que esperan avanzar." action={
      <div className="flex gap-2 items-center flex-wrap">
        <div
          className="inline-flex items-center rounded-md border border-stone-200 bg-white overflow-hidden text-xs"
          role="group"
          aria-label="Modo de visualización"
          data-testid="pipeline-view-toggle"
        >
          <button
            type="button"
            onClick={() => setViewModePersisted("stages")}
            aria-pressed={viewMode === "stages"}
            data-testid="pipeline-view-stages"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 transition-colors ${
              viewMode === "stages"
                ? "bg-stone-900 text-white"
                : "text-stone-600 hover:bg-stone-50"
            }`}
            title="Vista por etapas (vertical)"
          >
            <Rows size={13} weight={viewMode === "stages" ? "fill" : "regular"} />
            Etapas
          </button>
          <button
            type="button"
            onClick={() => setViewModePersisted("kanban")}
            aria-pressed={viewMode === "kanban"}
            data-testid="pipeline-view-kanban"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 transition-colors ${
              viewMode === "kanban"
                ? "bg-stone-900 text-white"
                : "text-stone-600 hover:bg-stone-50"
            }`}
            title="Vista kanban (horizontal)"
          >
            <SquaresFour size={13} weight={viewMode === "kanban" ? "fill" : "regular"} />
            Kanban
          </button>
        </div>
        <select
          value={ownerFilter}
          onChange={(e) => setOwnerFilterPersisted(e.target.value)}
          className="dv-input text-xs py-1.5 max-w-[180px]"
          data-testid="leads-owner-filter"
          title="Filtrar por responsable"
        >
          <option value="all">Todos los responsables</option>
          {currentUser?.user_id && <option value="mine">Mis leads</option>}
          <option value="unassigned">Sin asignar</option>
          {assignees.length > 0 && <option disabled>──────────</option>}
          {assignees.map(u => (
            <option key={u.user_id} value={u.user_id}>{u.name}</option>
          ))}
        </select>
        <a
          href={`${API}/leads/export.csv?include_archived=false`}
          target="_blank"
          rel="noopener noreferrer"
          className="dv-btn dv-btn-ghost"
          data-testid="lead-export-csv-btn"
          title="Exportar leads a CSV"
        >
          <DownloadSimple size={16} /> Exportar CSV
        </a>
        <button onClick={() => setShowForm(true)} className="dv-btn text-white" style={{ background: ACCENT }} data-testid="lead-new-btn">
          <Plus size={16} /> Nuevo lead
        </button>
      </div>
    }>
      {showForm && (
        <div className="dv-card mb-6">
          <form onSubmit={add} className="grid sm:grid-cols-2 gap-3">
            <input className="dv-input" placeholder="Nombre *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="lead-name" />
            <input className="dv-input" placeholder="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="lead-email" />
            <input className="dv-input" placeholder="Teléfono / WhatsApp" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone" />
            <select className="dv-input" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} data-testid="lead-source">
              <option value="web">Formulario web</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="gmail">Gmail</option>
              <option value="sheets">Google Sheets</option>
              <option value="referral">Referido</option>
            </select>
            <input className="dv-input" placeholder="Villa de interés" value={form.interest} onChange={e => setForm({ ...form, interest: e.target.value })} data-testid="lead-interest" />
            <textarea className="dv-input sm:col-span-2" rows="2" placeholder="Notas" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            <div className="sm:col-span-2 flex gap-3">
              <button className="dv-btn dv-btn-primary flex-1" data-testid="lead-save">Guardar</button>
              <button type="button" onClick={() => setShowForm(false)} className="dv-btn dv-btn-ghost">Cancelar</button>
            </div>
            {msg && <div className="sm:col-span-2 text-red-600 text-sm">{msg}</div>}
          </form>
        </div>
      )}

      {/* Bulk WhatsApp toolbar — hidden until the operator turns on selection
          mode. Kept above the search bar so it's obvious what the current
          selection covers. */}
      <div className="flex items-center flex-wrap gap-2 mb-3" data-testid="bulk-whatsapp-toolbar">
        <button
          type="button"
          onClick={toggleSelectionMode}
          className={`dv-btn text-sm inline-flex items-center gap-1.5 ${
            selectionMode ? "dv-btn-primary" : "dv-btn-ghost"
          }`}
          data-testid="bulk-whatsapp-toggle-mode"
        >
          <WhatsappLogo size={14} weight={selectionMode ? "fill" : "regular"} />
          {selectionMode ? "Salir de selección" : "Seleccionar para WhatsApp"}
        </button>
        {selectionMode && (
          <>
            <span className="text-xs text-stone-500" data-testid="bulk-whatsapp-count">
              {selectedIds.size} seleccionado{selectedIds.size === 1 ? "" : "s"}
              {selectedIds.size > 0 && ` · ${validSelectedCount} con teléfono válido`}
            </span>
            <button
              type="button"
              onClick={selectVisible}
              className="text-xs text-stone-600 hover:text-stone-900 underline"
              data-testid="bulk-whatsapp-select-visible"
            >
              Seleccionar visibles
            </button>
            <button
              type="button"
              onClick={clearSelection}
              disabled={selectedIds.size === 0}
              className="text-xs text-stone-500 hover:text-stone-900 underline disabled:opacity-40 disabled:no-underline"
              data-testid="bulk-whatsapp-clear"
            >
              Limpiar selección
            </button>
            <button
              type="button"
              onClick={() => setBulkOpen(true)}
              disabled={selectedIds.size === 0}
              className="dv-btn dv-btn-primary text-sm inline-flex items-center gap-1.5 ml-auto"
              style={{ background: "#25D366", color: "#fff", opacity: selectedIds.size === 0 ? 0.4 : 1 }}
              data-testid="bulk-whatsapp-open"
            >
              <WhatsappLogo size={14} weight="fill" /> Enviar WhatsApp
            </button>
          </>
        )}
      </div>

      {/* Free-text search — filters the already-visible leads (owner +
          attention + facets), so an active pipeline filter still constrains
          the search space. */}
      <div className="flex items-center flex-wrap gap-2 mb-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre, email, teléfono o canal…"
          className="dv-input text-sm w-full max-w-md"
          data-testid="leads-search-input"
          aria-label="Buscar leads"
        />
        {search && (
          <>
            <button
              type="button"
              onClick={() => setSearch("")}
              className="text-xs text-stone-500 hover:text-stone-900 underline"
              data-testid="leads-search-clear"
            >
              Limpiar
            </button>
            <span
              className="text-xs text-stone-500"
              data-testid="leads-search-count"
            >
              Mostrando {searchedLeads.length} de {leads.length} leads
            </span>
          </>
        )}
      </div>

      {/* Attention filter — global toggle + granular facets */}
      <div className="flex items-center flex-wrap gap-2 mb-4">
        <button
          type="button"
          onClick={() => onlyAttention ? clearAttention() : setOnlyAttention(true)}
          data-testid="leads-attention-toggle"
          className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full transition-colors ${
            onlyAttention
              ? "bg-amber-100 text-amber-900 border border-amber-300"
              : "bg-stone-100 text-stone-600 border border-stone-200 hover:bg-stone-200"
          }`}
        >
          <Warning size={12} weight="bold" />
          Atención requerida
          {attentionCount > 0 && (
            <span className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] ${
              onlyAttention ? "bg-amber-900 text-amber-100" : "bg-stone-900 text-white"
            }`}>
              {attentionCount}
            </span>
          )}
        </button>

        <label className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border border-stone-200 bg-white text-stone-600 hover:bg-stone-50 cursor-pointer select-none"
               data-testid="leads-include-archived-label">
          <input
            type="checkbox"
            className="w-3 h-3"
            data-testid="leads-include-archived-toggle"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          Ver archivados
        </label>

        {/* Facet sub-chips */}
        {ATTENTION_FACETS.map(f => {
          const active = activeFacets.has(f.key);
          const count = facetCounts[f.key] || 0;
          if (count === 0 && !active) return null;
          const baseTone =
            f.tone === "red"
              ? (active ? "bg-red-100 text-red-900 border-red-300"
                        : "bg-white text-red-700 border-red-200 hover:bg-red-50")
              : (active ? "bg-amber-100 text-amber-900 border-amber-300"
                        : "bg-white text-amber-700 border-amber-200 hover:bg-amber-50");
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => toggleFacet(f.key)}
              data-testid={`leads-facet-${f.key}`}
              className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border transition ${baseTone}`}
            >
              {f.label}
              <span className={`inline-flex items-center justify-center min-w-[16px] h-[16px] rounded-full text-[10px] ${
                active ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"
              }`}>{count}</span>
            </button>
          );
        })}

        {(onlyAttention || activeFacets.size > 0) && (
          <span className="text-xs text-stone-500 ml-1">
            Mostrando {visibleLeads.length} de {leads.length}
          </span>
        )}
      </div>

      {search && searchedLeads.length === 0 ? (
        <div
          className="dv-card text-center py-8 text-stone-500 text-sm"
          data-testid="leads-search-empty"
        >
          No hay leads que coincidan con la búsqueda.
        </div>
      ) : viewMode === "stages" ? (
        <PipelineStageAccordion
          leads={searchedLeads}
          assignees={assignees}
          onOpenLead={(id) => setOpenLeadId(id)}
          onPatch={patchLead}
          selectable={selectionMode}
          selectedIds={selectedIds}
          onToggleSelect={toggleLeadSelected}
        />
      ) : (
        /* Visible pipeline (9 + Lead frío). Legacy statuses are rebucketed
            via visibleStep() so dossier_pending / dossier / reservation_sent
            show up inside their canonical adjacent column. */
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-2 px-2" data-testid="kanban-scroll">
          {VISIBLE_PIPELINE_WITH_COLD.map(st => {
            const items = searchedLeads.filter(l => visibleStep(l.status) === st.key);
            const isFollowup = st.key === "in_followup";
            return (
              <div
                key={st.key}
                className={`shrink-0 w-[240px] bg-stone-50 rounded-lg p-3 border ${
                  isFollowup ? "border-t-2 border-t-amber-400 border-stone-200" : "border-stone-200"
                }`}
                data-testid={`kanban-col-${st.key}`}
              >
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: st.dotColor }} />
                    <div className="text-xs uppercase tracking-widest text-stone-600">{st.label}</div>
                  </div>
                  <div className="text-xs text-stone-400">{items.length}</div>
                </div>
                <div className="space-y-2">
                  {items.map(l => (
                    <LeadCard
                      key={l.lead_id}
                      lead={l}
                      assignees={assignees}
                      onOpen={() => setOpenLeadId(l.lead_id)}
                      onPatch={patchLead}
                      selectable={selectionMode}
                      selected={selectedIds.has(l.lead_id)}
                      onToggleSelect={toggleLeadSelected}
                    />
                  ))}
                  {items.length === 0 && <div className="text-[11px] text-stone-300 text-center py-2">—</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <LeadSidePanel
        leadId={openLeadId}
        assignees={assignees}
        open={!!openLeadId}
        onClose={() => setOpenLeadId(null)}
        onChanged={load}
      />

      {bulkOpen && (
        <BulkWhatsappModal
          leads={selectedLeads}
          onClose={() => setBulkOpen(false)}
        />
      )}
    </Section>
  );
}
